import React, { useState, useMemo } from "react";
import * as Select from "@radix-ui/react-select";
import { TbChevronDown, TbCheck, TbSearch } from "react-icons/tb";
import { clsx } from "clsx";

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SearchableSelectProps {
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  options: SelectOption[];
  disabled?: boolean;
  hasError?: boolean;
  className?: string;
  searchPlaceholder?: string;
}

export const SearchableSelect = React.forwardRef<
  React.ElementRef<typeof Select.Trigger>,
  SearchableSelectProps
>(
  (
    {
      value,
      onValueChange,
      placeholder = "Select",
      options,
      disabled = false,
      hasError = false,
      className,
      searchPlaceholder = "Search...",
      ...props
    },
    ref
  ) => {
    const [searchTerm, setSearchTerm] = useState("");

    // Filter options based on search term
    const filteredOptions = useMemo(() => {
      if (!searchTerm) return options.filter((option) => option.value !== "");

      return options
        .filter((option) => option.value !== "")
        .filter((option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [options, searchTerm]);

    const handleOpenChange = (open: boolean) => {
      if (!open) {
        setSearchTerm(""); // Clear search when closing
      }
    };

    return (
      <Select.Root
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
        onOpenChange={handleOpenChange}
      >
        <Select.Trigger
          ref={ref}
          className={clsx(
            "flex h-10 w-full items-center justify-between rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground",
            "focus:outline-none focus:bg-white",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "data-[placeholder]:text-gray-500",
            hasError && "border-red-400",
            className
          )}
          {...props}
        >
          <Select.Value placeholder={placeholder} />
          <Select.Icon asChild>
            <TbChevronDown className="h-4 w-4 opacity-50" />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content className="relative z-[1002] max-h-96 min-w-[8rem] overflow-hidden rounded-lg border border-gray-200 bg-white text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2">
            <Select.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
              <TbChevronDown className="h-4 w-4 rotate-180" />
            </Select.ScrollUpButton>

            {/* Search Input */}
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <TbSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none"
                  onClick={(e) => e.stopPropagation()} // Prevent closing dropdown when clicking input
                />
              </div>
            </div>

            <Select.Viewport className="p-1">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <Select.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className="relative flex w-full cursor-default select-none items-center rounded-md py-2 pl-8 pr-2 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100 data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                  >
                    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                      <Select.ItemIndicator>
                        <TbCheck className="h-4 w-4" />
                      </Select.ItemIndicator>
                    </span>
                    <Select.ItemText>{option.label}</Select.ItemText>
                  </Select.Item>
                ))
              ) : (
                <div className="py-6 text-center text-sm text-gray-500">
                  No results found
                </div>
              )}
            </Select.Viewport>
            <Select.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
              <TbChevronDown className="h-4 w-4" />
            </Select.ScrollDownButton>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    );
  }
);

SearchableSelect.displayName = "SearchableSelect";
